/**
 * Demo data for the landing page showcases.
 * All data is fake and used purely for demonstration purposes.
 */

// Generate TWR performance data for the last 365 days
function generateTwrData(): { timestamp: string; twr: number; benchmark: number }[] {
  const data: { timestamp: string; twr: number; benchmark: number }[] = [];
  const now = new Date();
  
  let portfolioValue = 0; // Starting at 0% return
  let benchmarkValue = 0;
  
  for (let i = 365; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    
    // Simulate daily returns with some volatility
    const portfolioDaily = (Math.random() - 0.45) * 0.8; // Slightly positive bias
    const benchmarkDaily = (Math.random() - 0.47) * 0.6; // Slightly less positive bias
    
    portfolioValue += portfolioDaily;
    benchmarkValue += benchmarkDaily;
    
    // Add some correlation and trends
    if (i < 180) {
      portfolioValue += 0.03; // Bull run in second half
      benchmarkValue += 0.02;
    }
    
    data.push({
      timestamp: date.toISOString(),
      twr: Math.round(portfolioValue * 100) / 100,
      benchmark: Math.round(benchmarkValue * 100) / 100,
    });
  }
  
  return data;
}

export const demoTwrData = generateTwrData();

// Get the last values for display
export const demoTwrSummary = {
  portfolioReturn: demoTwrData[demoTwrData.length - 1]?.twr ?? 24.5,
  benchmarkReturn: demoTwrData[demoTwrData.length - 1]?.benchmark ?? 18.2,
  benchmarkSymbol: "SPY",
  benchmarkName: "S&P 500",
};

// Portfolio allocation data
export const demoAllocationData = [
  { name: "Apple Inc.", symbol: "AAPL", value: 45000, type: "stock", color: "hsl(158, 75%, 55%)" },
  { name: "NVIDIA Corp.", symbol: "NVDA", value: 38000, type: "stock", color: "hsl(210, 70%, 60%)" },
  { name: "Microsoft Corp.", symbol: "MSFT", value: 32000, type: "stock", color: "hsl(280, 60%, 60%)" },
  { name: "Bitcoin", symbol: "BTC", value: 28000, type: "crypto", color: "hsl(340, 70%, 55%)" },
  { name: "Vanguard S&P 500 ETF", symbol: "VOO", value: 25000, type: "etf", color: "hsl(30, 85%, 55%)" },
  { name: "Alphabet Inc.", symbol: "GOOGL", value: 22000, type: "stock", color: "hsl(190, 80%, 45%)" },
  { name: "Ethereum", symbol: "ETH", value: 15000, type: "crypto", color: "hsl(50, 90%, 50%)" },
  { name: "Cash", symbol: "USD", value: 10000, type: "fiat", color: "hsl(120, 50%, 50%)" },
];

export const demoAllocationByType = [
  { name: "Stocks", key: "stock", value: 137000, percentage: 63.7, color: "hsl(158, 75%, 55%)" },
  { name: "Crypto", key: "crypto", value: 43000, percentage: 20.0, color: "hsl(340, 70%, 55%)" },
  { name: "ETFs", key: "etf", value: 25000, percentage: 11.6, color: "hsl(30, 85%, 55%)" },
  { name: "Cash", key: "fiat", value: 10000, percentage: 4.7, color: "hsl(120, 50%, 50%)" },
];

export const demoAllocationTotal = 215000;

// Screener data by asset type (for tab filtering)
export const demoScreenerData = [
  {
    type: "stocks",
    symbol: "AAPL",
    name: "Apple Inc.",
    sector: "Technology",
    marketCap: 3450000000000,
    pe: 28.5,
    change: 1.24,
    divYield: 0.0044,
    roe: 0.147,
    currency: "$",
    logo: "https://img.logo.dev/apple.com?format=png&size=96",
  },
  {
    type: "stocks",
    symbol: "NVDA",
    name: "NVIDIA Corporation",
    sector: "Technology",
    marketCap: 3200000000000,
    pe: 65.2,
    change: 2.87,
    divYield: 0.0003,
    roe: 0.693,
    currency: "$",
    logo: "https://img.logo.dev/nvidia.com?format=png&size=96",
  },
  {
    type: "stocks",
    symbol: "MSFT",
    name: "Microsoft Corporation",
    sector: "Technology",
    marketCap: 3100000000000,
    pe: 35.8,
    change: -0.45,
    divYield: 0.0072,
    roe: 0.352,
    currency: "$",
    logo: "https://img.logo.dev/microsoft.com?format=png&size=96",
  },
  {
    type: "stocks",
    symbol: "GOOGL",
    name: "Alphabet Inc.",
    sector: "Communication",
    marketCap: 2100000000000,
    pe: 24.3,
    change: 0.92,
    divYield: 0.0045,
    roe: 0.268,
    currency: "$",
    logo: "https://img.logo.dev/google.com?format=png&size=96",
  },
  {
    type: "stocks",
    symbol: "AMZN",
    name: "Amazon.com Inc.",
    sector: "Consumer Cyclical",
    marketCap: 2050000000000,
    pe: 42.1,
    change: -1.12,
    divYield: null,
    roe: 0.198,
    currency: "$",
    logo: "https://img.logo.dev/amazon.com?format=png&size=96",
  },
  {
    type: "stocks",
    symbol: "META",
    name: "Meta Platforms Inc.",
    sector: "Communication",
    marketCap: 1450000000000,
    pe: 26.8,
    change: 1.56,
    divYield: 0.0035,
    roe: 0.312,
    currency: "$",
    logo: "https://img.logo.dev/meta.com?format=png&size=96",
  },
  {
    type: "etf",
    symbol: "VOO",
    name: "Vanguard S&P 500 ETF",
    sector: "Large Blend",
    marketCap: 520000000000,
    pe: null,
    change: 0.36,
    divYield: 0.0128,
    roe: null,
    currency: "$",
    logo: "https://img.logo.dev/vanguard.com?format=png&size=96",
  },
  {
    type: "etf",
    symbol: "SPY",
    name: "SPDR S&P 500 ETF Trust",
    sector: "Large Blend",
    marketCap: 560000000000,
    pe: null,
    change: 0.41,
    divYield: 0.0122,
    roe: null,
    currency: "$",
    logo: "https://img.logo.dev/ssga.com?format=png&size=96",
  },
  {
    type: "etf",
    symbol: "QQQ",
    name: "Invesco QQQ Trust",
    sector: "Large Growth",
    marketCap: 290000000000,
    pe: null,
    change: 0.64,
    divYield: 0.0064,
    roe: null,
    currency: "$",
    logo: "https://img.logo.dev/invesco.com?format=png&size=96",
  },
  {
    type: "crypto",
    symbol: "BTC",
    name: "Bitcoin",
    sector: "Crypto",
    marketCap: 1300000000000,
    pe: null,
    change: 2.31,
    divYield: null,
    roe: null,
    currency: "$",
    logo: "/btc.png",
  },
  {
    type: "crypto",
    symbol: "ETH",
    name: "Ethereum",
    sector: "Crypto",
    marketCap: 420000000000,
    pe: null,
    change: 1.58,
    divYield: null,
    roe: null,
    currency: "$",
    logo: "/eth.png",
  },
  {
    type: "crypto",
    symbol: "SOL",
    name: "Solana",
    sector: "Crypto",
    marketCap: 82000000000,
    pe: null,
    change: -0.94,
    divYield: null,
    roe: null,
    currency: "$",
    logo: "/sol.png",
  },
];

// Watchlist data
export const demoWatchlists = [
  {
    name: "Tech Giants",
    items: [
      { symbol: "AAPL", name: "Apple Inc.", price: 178.52, change: 1.24, logo: "https://img.logo.dev/apple.com?format=png&size=96" },
      { symbol: "NVDA", name: "NVIDIA Corp.", price: 875.28, change: 2.87, logo: "https://img.logo.dev/nvidia.com?format=png&size=96" },
      { symbol: "MSFT", name: "Microsoft Corp.", price: 415.60, change: -0.45, logo: "https://img.logo.dev/microsoft.com?format=png&size=96" },
      { symbol: "GOOGL", name: "Alphabet Inc.", price: 175.98, change: 0.92, logo: "https://img.logo.dev/google.com?format=png&size=96" },
    ],
  },
  {
    name: "Dividend Kings",
    items: [
      { symbol: "JNJ", name: "Johnson & Johnson", price: 152.34, change: 0.28, logo: "https://img.logo.dev/jnj.com?format=png&size=96" },
      { symbol: "PG", name: "Procter & Gamble", price: 168.45, change: 0.15, logo: "https://img.logo.dev/pg.com?format=png&size=96" },
      { symbol: "KO", name: "Coca-Cola Co.", price: 62.18, change: -0.32, logo: "https://img.logo.dev/coca-cola.com?format=png&size=96" },
    ],
  },
  {
    name: "Crypto Picks",
    items: [
      { symbol: "BTC", name: "Bitcoin", price: 67542.00, change: 3.45, logo: "/btc.png" },
      { symbol: "ETH", name: "Ethereum", price: 3456.78, change: 2.12, logo: "/eth.png" },
      { symbol: "SOL", name: "Solana", price: 178.92, change: -1.28, logo: "/sol.png" },
    ],
  },
];

// Financial charts demo data (Apple-like, ~8 years of annual data)
export const demoFinancialChartsData = {
  company: {
    symbol: "AAPL",
    name: "Apple Inc.",
    logo: "https://img.logo.dev/apple.com?format=png&size=96",
  },
  charts: {
    revenue: [
      { periodKey: "2017", revenue: 229234000000 },
      { periodKey: "2018", revenue: 265595000000 },
      { periodKey: "2019", revenue: 260174000000 },
      { periodKey: "2020", revenue: 274515000000 },
      { periodKey: "2021", revenue: 365817000000 },
      { periodKey: "2022", revenue: 394328000000 },
      { periodKey: "2023", revenue: 383285000000 },
      { periodKey: "2024", revenue: 391035000000 },
    ],
    net_income: [
      { periodKey: "2017", net_income: 48351000000 },
      { periodKey: "2018", net_income: 59531000000 },
      { periodKey: "2019", net_income: 55256000000 },
      { periodKey: "2020", net_income: 57411000000 },
      { periodKey: "2021", net_income: 94680000000 },
      { periodKey: "2022", net_income: 99803000000 },
      { periodKey: "2023", net_income: 96995000000 },
      { periodKey: "2024", net_income: 93736000000 },
    ],
    gross_margin: [
      { periodKey: "2017", gross_margin: 38.5 },
      { periodKey: "2018", gross_margin: 38.3 },
      { periodKey: "2019", gross_margin: 37.8 },
      { periodKey: "2020", gross_margin: 38.2 },
      { periodKey: "2021", gross_margin: 41.8 },
      { periodKey: "2022", gross_margin: 43.3 },
      { periodKey: "2023", gross_margin: 44.1 },
      { periodKey: "2024", gross_margin: 46.2 },
    ],
    free_cash_flow: [
      { periodKey: "2017", free_cash_flow: 51774000000 },
      { periodKey: "2018", free_cash_flow: 64121000000 },
      { periodKey: "2019", free_cash_flow: 58896000000 },
      { periodKey: "2020", free_cash_flow: 73365000000 },
      { periodKey: "2021", free_cash_flow: 92953000000 },
      { periodKey: "2022", free_cash_flow: 111443000000 },
      { periodKey: "2023", free_cash_flow: 99584000000 },
      { periodKey: "2024", free_cash_flow: 108807000000 },
    ],
    eps: [
      { periodKey: "2017", eps: 2.30 },
      { periodKey: "2018", eps: 2.98 },
      { periodKey: "2019", eps: 2.97 },
      { periodKey: "2020", eps: 3.28 },
      { periodKey: "2021", eps: 5.61 },
      { periodKey: "2022", eps: 6.11 },
      { periodKey: "2023", eps: 6.13 },
      { periodKey: "2024", eps: 6.08 },
    ],
    price_to_earnings: [
      { periodKey: "2017", price_to_earnings: 18.4 },
      { periodKey: "2018", price_to_earnings: 13.2 },
      { periodKey: "2019", price_to_earnings: 24.8 },
      { periodKey: "2020", price_to_earnings: 40.5 },
      { periodKey: "2021", price_to_earnings: 31.6 },
      { periodKey: "2022", price_to_earnings: 21.2 },
      { periodKey: "2023", price_to_earnings: 31.3 },
      { periodKey: "2024", price_to_earnings: 37.8 },
    ],
  },
};

// Sector allocation demo data (for treemap)
export const demoSectorAllocationData = [
  { name: "Technology", size: 95000, percentage: 44.2, color: "hsl(158, 75%, 55%)" },
  { name: "Communication", size: 22000, percentage: 10.2, color: "hsl(210, 70%, 60%)" },
  { name: "Consumer Cyclical", size: 18000, percentage: 8.4, color: "hsl(280, 60%, 60%)" },
  { name: "Healthcare", size: 16000, percentage: 7.4, color: "hsl(340, 70%, 55%)" },
  { name: "Financial", size: 14000, percentage: 6.5, color: "hsl(30, 85%, 55%)" },
  { name: "Industrials", size: 12000, percentage: 5.6, color: "hsl(190, 80%, 45%)" },
  { name: "Energy", size: 10000, percentage: 4.7, color: "hsl(50, 90%, 50%)" },
  { name: "Real Estate", size: 8000, percentage: 3.7, color: "hsl(0, 70%, 55%)" },
  { name: "Utilities", size: 6000, percentage: 2.8, color: "hsl(120, 50%, 50%)" },
  { name: "Materials", size: 5500, percentage: 2.6, color: "hsl(260, 70%, 65%)" },
  { name: "Consumer Staples", size: 4500, percentage: 2.1, color: "hsl(15, 80%, 60%)" },
  { name: "Other", size: 4000, percentage: 1.9, color: "hsl(180, 60%, 50%)" },
];

export const demoSectorTotal = demoSectorAllocationData.reduce((sum, s) => sum + s.size, 0);
